/**
 * @file Build the JSON document and supporting Markdown files written into
 * the export zip, following plan/docs/export_schema_v1.md (schema_v=1).
 *
 * The Firestore Timestamp objects are normalized to ISO 8601 UTC strings.
 * Encrypted text fields are replaced with their decrypted plain string,
 * and `wrapped_dek` is preserved verbatim for Phase-3 rewrap support.
 */

import { Timestamp } from "firebase/firestore";
import type {
  DigestDoc,
  EntryDoc,
  ExportComment,
  ExportReaction,
  FamilyDoc,
  FamilyMembershipDoc,
  ObservationDoc,
  PersonDoc,
} from "../types";

export interface DecryptedEntry extends Omit<EntryDoc, "text"> {
  text: string; // 平文
  /** epic_18 task_459: スタンプ (平文展開、actor は role hint のみ)。 */
  reactions: ExportReaction[];
  /** epic_18 task_459: コメント (平文展開、tombstone は text:null)。 */
  comments: ExportComment[];
}

export interface DecryptedObservation extends Omit<ObservationDoc, "text"> {
  text: string;
}

export interface DecryptedDigest extends Omit<DigestDoc, "text"> {
  text: string;
}

export interface ExportDataJson {
  schema_v: 1;
  exported_at: string; // ISO 8601 UTC
  family: FamilyDoc;
  persons: PersonDoc[];
  memberships: FamilyMembershipDoc[];
  entries: DecryptedEntry[];
  observations: DecryptedObservation[];
  digests: DecryptedDigest[];
}

/** Recursively replace Firestore Timestamps with ISO strings. */
export function normalizeTimestamps<T>(value: T): T {
  if (value instanceof Timestamp) {
    return value.toDate().toISOString() as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeTimestamps) as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = normalizeTimestamps(v);
    }
    return out as T;
  }
  return value;
}

export function buildDataJson(args: {
  family: FamilyDoc;
  persons: PersonDoc[];
  memberships: FamilyMembershipDoc[];
  entries: DecryptedEntry[];
  observations: DecryptedObservation[];
  digests: DecryptedDigest[];
}): ExportDataJson {
  return {
    schema_v: 1,
    exported_at: new Date().toISOString(),
    family: normalizeTimestamps(args.family),
    persons: normalizeTimestamps(args.persons),
    memberships: normalizeTimestamps(args.memberships),
    entries: normalizeTimestamps(args.entries),
    observations: normalizeTimestamps(args.observations),
    digests: normalizeTimestamps(args.digests),
  };
}

export interface ExportSummary {
  entries: number;
  observations: number;
  digests: number;
  persons: number;
  mediaFiles: number;
  mediaBytes: number;
  totalBytes: number;
}

export function renderReadmeMarkdown(summary: ExportSummary): string {
  return [
    "# Berry Buds データエクスポート",
    "",
    "本 zip はあなたのご家族の記録 (ひとコマ・気づき・通信簿・メディア) の手元バックアップです。",
    "",
    "## 同梱内容",
    "",
    "- `data.json` — メタデータ (家族・人物・メンバーシップ・ひとコマ・気づき・通信簿)",
    "  - 各ひとコマ (`entries[].reactions` / `entries[].comments`) に家族のスタンプ・コメントを同梱",
    "- `schema.md` — `data.json` の構造仕様 (schema_v=1)",
    "- `media/` — 復号済みメディアファイル本体 (画像・動画・音声)",
    "",
    "## サマリ",
    "",
    `- ひとコマ: ${summary.entries} 件`,
    `- 気づき: ${summary.observations} 件`,
    `- 通信簿: ${summary.digests} 件`,
    `- 人物: ${summary.persons} 件`,
    `- メディア: ${summary.mediaFiles} 件 (${formatBytes(summary.mediaBytes)})`,
    `- 全体サイズ: ${formatBytes(summary.totalBytes)}`,
    "",
    "## 注意事項",
    "",
    "- 復号済み平文データを含みます。共有 PC では取り扱いに注意してください。",
    "- 本 zip は暗号化されていません。OS のディスク暗号化や個人ストレージで保護してください。",
    "- 本データの構造詳細は `schema.md` および `plan/docs/export_schema_v1.md` を参照してください。",
    "",
  ].join("\n");
}

export function renderSchemaMarkdown(): string {
  return [
    "# data.json schema (schema_v = 1)",
    "",
    "詳細は `plan/docs/export_schema_v1.md` を参照。本ファイルはエクスポート時点の",
    "実装と整合する形での要約です。",
    "",
    "## トップレベル",
    "",
    "```jsonc",
    "{",
    '  "schema_v": 1,',
    '  "exported_at": "2026-04-26T12:34:56.789Z",  // ISO 8601 UTC',
    '  "family":      Family,',
    '  "persons":     Person[],',
    '  "memberships": FamilyMembership[],',
    '  "entries":     Entry[],',
    '  "observations": Observation[],',
    '  "digests":     Digest[]',
    "}",
    "```",
    "",
    "## Entry / Observation / Digest の暗号化",
    "",
    "Firestore 上では `text` が DEK で暗号化されているが、本 zip 内では **平文** の",
    "`text` が直接埋め込まれている。`wrapped_dek` フィールドは Phase 3 の鍵 rotation",
    "互換のためそのまま残置している (再復号には新たな KEK が必要)。",
    "",
    "## スタンプ / コメント (Entry 配下)",
    "",
    "各 `Entry` には家族メンバーのスタンプとコメントが配下に **平文展開** で同梱される",
    "(epic_18 / task_459)。プライバシー保護のため、誰が反応したかの **平文 person_id は",
    "含めず**、`actor_display_hint` に家族内の役割スナップショット (例: `parent`) のみを載せる。",
    "",
    "```jsonc",
    '"entries": [{',
    "  // ...Entry の他フィールド",
    '  "reactions": [{',
    '    "kind": "like",                 // like / moved / seen',
    '    "actor_display_hint": "parent", // role snapshot (平文 person_id は含めない)',
    '    "created_at": "2026-06-21T00:00:00.000Z"',
    "  }],",
    '  "comments": [{',
    '    "id": "<uuid>",                // commentId。corrects/thread_id (UUID) で編集系譜を辿れる',
    '    "text": "かわいい！",            // 本文 (平文)。tombstone 済みは null',
    '    "actor_display_hint": "parent", // role snapshot。tombstone 済みは null',
    '    // 編集済みコメントは旧 version も含まれる (append-only)。corrects/thread_id (UUID) で系譜を辿れる。',
    '    "created_at": "2026-06-21T00:00:00.000Z",',
    '    "deleted_at": "2026-06-22T00:00:00.000Z" // 削除済みのみ存在',
    "  }]",
    "}]",
    "```",
    "",
    "削除済み (soft-delete) コメントは本文と鍵が破棄されているため `text: null` /",
    "`actor_display_hint: null` で記録し、`deleted_at` を残す (会話の存在のみ保持)。",
    "",
    "## メディア",
    "",
    "`Entry.media[]` の各エントリは `storage_path` を含む。実バイト列は zip 内の",
    "`media/{entryId}_{index}.{ext}` に **平文** で配置される。",
    "",
  ].join("\n");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  for (const unit of units) {
    if (value < 1024) return `${value.toFixed(1)} ${unit}`;
    value /= 1024;
  }
  return `${value.toFixed(1)} PB`;
}

/** Resolve the file extension for a media item, with a sensible default. */
export function mediaExtension(item: {
  type: string;
  storage_path: string;
  ext?: string | undefined;
}): string {
  if (item.ext) return item.ext.replace(/^\./, "");
  const fromPath = /\.([a-zA-Z0-9]{1,5})$/.exec(item.storage_path);
  if (fromPath) return fromPath[1]!;
  switch (item.type) {
    case "image":
      return "jpg";
    case "video":
      return "mp4";
    case "audio":
      return "m4a";
    default:
      return "bin";
  }
}
